import React, { FC } from 'react';

import classNames from 'classnames';

import { Document } from '@contentful/rich-text-types';

import { Badge, HeaderText, RichText } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { Image } from '@/components/ui/Image';

import { CONTENT_TYPE_MEDIA_TEXT_TITLE } from '@/core/constants';
import { TypeImage, TypeMediaTextTitleFields } from '@/core/generated-types';

import MediaTextTitleModal from '../MediaTextTitle/MediaTextTitleModal';
import { RichTextHeading } from '../RichTextHeading';
import { UniversalCta } from '@/components/common/UniversalCta';

const ProductBlock: FC<TypeMediaTextTitleFields> = (props) => {
  const {
    layoutType,
    image,
    textBody,
    textHeading,
    id,
    narrowMobilePadding,
    badge,
    modal,
    italicTextBody,
    componentAlignment,
    mobileAlignment,
    cta,
  } = props;

  const blockSmall = layoutType === CONTENT_TYPE_MEDIA_TEXT_TITLE.SMALL;
  const blockMedium = layoutType === CONTENT_TYPE_MEDIA_TEXT_TITLE.MEDIUM;
  const blockLarge = !blockMedium && !blockSmall;
  const isCentered =
    componentAlignment === 'center' || mobileAlignment === 'center';

  return (
    <div
      id={id}
      className={classNames(
        'flex w-full items-start gap-2 text-center h-full',
        {
          'flex-row': !blockLarge,
          'mx-auto flex-col md:items-center': blockLarge,
          'items-center': blockLarge && isCentered,
          'items-start': blockLarge && !isCentered,
        },
      )}
    >
      <div
        className={classNames('flex flex-none', {
          'h-[48px] w-[48px] items-start md:h-[72px] md:w-[72px]': blockSmall,
          'h-[72px] w-[72px] items-start': blockMedium,
          'lg:h-88 lg:w-88 items-center mb-2 lg:mb-4': blockLarge,
        })}
      >
        <div className={badge ? 'relative' : undefined}>
          {badge && (
            <div className="absolute z-[1] -top-4">
              <Badge {...badge.fields} />
            </div>
          )}

          {image && (
            <Image
              imageData={image as TypeImage}
              alt={image.fields?.altText}
              width={!blockLarge ? 72 : 360}
              height={!blockLarge ? 72 : 360}
              className="h-auto w-full"
            />
          )}
        </div>
      </div>
      <div
        className={classNames('flex flex-col gap-1 text-center', {
          'justify-center text-center max-w-[384px]': blockLarge,
          'justify-left text-left': !blockLarge,
          'px-12': !narrowMobilePadding,
        })}
      >
        <HeaderText
          size={HeaderSize.H3}
          text={textHeading}
          classes={`${!blockLarge ? 'text-left' : 'text-center'}`}
        />

        {textBody && (
          <div
            className={classNames('leading-6', {
              'text-left': !blockLarge,
              'text-base md:text-lg': narrowMobilePadding,
              'text-lg': !narrowMobilePadding,
            })}
            data-testid={`product-block-text-paragraph`}
          >
            {italicTextBody ? (
              <div className="text-left md:text-center md:px-6">
                <RichTextHeading content={textBody as Document} />
              </div>
            ) : (
              <RichText content={textBody as Document} />
            )}
          </div>
        )}
      </div>
      {modal && <MediaTextTitleModal fields={modal.fields} />}
      {cta && (
        <UniversalCta
          link={cta}
          defaultButtonStyle="Primary"
          className="my-4 mx-20 w-5/6 lg:w-auto lg:grow lg:basis-0"
        />
      )}
    </div>
  );
};

export default ProductBlock;
